/**
 * The expense ceiling, and whether the category budgets tally with it.
 *
 * Savings are committed *before* expenditure, not a kind of it: what is put away
 * comes off the salary, and what remains is what there is to spend. So the
 * headline budget is `salary − Σ savings budgets` rather than the sum of the
 * expenditure budgets it used to be (scope expansion 2026-07-31).
 *
 * Every figure here is **derived per period** from versioned rows. There is no
 * stored ceiling and there must not be one — `DATA_MODEL.md` §7.2's rule against
 * a `budget_total` column applies to this exactly as it applies to the sum it
 * replaced.
 */

import { type Cents, cents } from "./money";

/**
 * A monthly amount projected out to a year — the budget sheet's
 * `total_at_12_months` column and `/savings`'s per-year figure, which must agree.
 */
export const PROJECTION_MONTHS = 12;

/**
 * One category's contribution to the allocation. `budget` is the **base** budget:
 * rollover carry is deliberately excluded for savings categories, because
 * nothing is ever logged against one, so a "carry surplus" mode would roll the
 * entire amount forward every period and compound the ceiling away to nothing.
 */
export interface AllocationRow {
  readonly isSavings: boolean;
  readonly budget: Cents | null;
}

/**
 * What is committed to savings this period.
 *
 * Zero rather than null when nothing is marked savings — unlike a budget, "no
 * savings" is a definite answer and subtracts a definite nothing.
 */
export function savingsCommitted(rows: readonly AllocationRow[]): Cents {
  let total = 0;

  for (const row of rows) {
    if (!row.isSavings || row.budget === null) continue;
    total += row.budget;
  }
  return cents(total);
}

/**
 * What there is to spend: salary less what is put away.
 *
 * Null when no salary is set, which must render as "no high-water mark" rather
 * than as zero, for the reason {@link salaryInForce} gives.
 *
 * Deliberately **not clamped at zero.** Committing more to savings than the
 * salary covers is a mistake worth seeing; clamping would hide it behind a
 * plausible-looking figure, and reporting it is the whole point of the flag.
 */
export function expenseCeiling(salary: Cents | null, committed: Cents): Cents | null {
  if (salary === null) return null;
  return cents(salary - committed);
}

/**
 * Ceiling less the expenditure budgets actually allocated against it.
 *
 * Positive means headroom not yet allocated; negative means the category budgets
 * promise more than the salary covers. Zero means they tally, which is the state
 * the user is aiming at.
 *
 * Null when either side is unknown — no salary, or no budgets set at all. There
 * is nothing to reconcile in either case, and a zero here would read as "they
 * tally" when the truth is "we cannot say" (INV-7).
 *
 * **`expenditureBase` must exclude rollover carry**, and the parameter is named
 * for it because getting this wrong is invisible. The ceiling is built from
 * *base* savings budgets, so it is a recurring amount; carry is an accumulating
 * one. Passing `base + carried` put the two on opposite sides of one comparison
 * and made the flag drift by the whole unspent budget every period, reporting a
 * mismatch that had not changed as one that was getting worse.
 */
export function allocationGap(ceiling: Cents | null, expenditureBase: Cents | null): Cents | null {
  if (ceiling === null || expenditureBase === null) return null;
  return cents(ceiling - expenditureBase);
}
